import {
  AddressEntryType,
  AutomationTriggerType,
  EmailTemplateType,
  ExternalCalendarProvider,
  FileOnRecordType,
  RecordTaskStatus,
  RecordTaskType,
  RecordTemplateApplicantType,
  RecordTypeCategory,
  RecordTypeStatusType,
  ViolationInspectionStatus,
  ViolationStatus,
} from 'types/graphql'

export const OrganizationId = 1

const Second = 1

export const SecondsInMinute = 60 * Second
export const SecondsInHour = 60 * SecondsInMinute
export const SecondsInDay = 24 * SecondsInHour

export const MillisecondsInSecond = 1000
export const MillisecondsInMinute = SecondsInMinute * MillisecondsInSecond
export const MillisecondsInHour = SecondsInHour * MillisecondsInSecond

export enum Environments {
  Development = 'Dev',
  Demo = 'Demo',
  Local = 'Local',
  Production = 'Prod',
  ProductionMirror = 'ProdCopy',
}

// When adding a variable here, be sure to allowlist it in redwood.toml at the root of this repo!
export const EnvVariables = {
  ApplicationAutoCheckTimeout: parseInt(process.env.APPLICATION_AUTO_CHECK_TIMEOUT || '45000', 10),
  ArcGISApiKey: process.env.ARCGIS_API_KEY || '',
  ClerkPublishableKey: process.env.CLERK_PUBLISHABLE_KEY || '',
  DatadogClientToken: process.env.DD_RUM_CLIENT_TOKEN,
  DatadogEnv: process.env.DD_ENV,
  DatadogRumEnabled: process.env.DD_ENABLE_RUM,
  DatadogService: process.env.DD_SERVICE,
  Environment: process.env.Environment as Environments,
  EvervaultApiKey: process.env.EVERVAULT_API_KEY,
  EvervaultAppId: process.env.EVERVAULT_APP_ID,
  EvervaultRelayHostname: process.env.EVERVAULT_RELAY_HOSTNAME,
  EvervaultTeamId: process.env.EVERVAULT_TEAM_ID,
  FileUploadApiKey: process.env.FILE_UPLOAD_API_KEY,
  FinixAppId: process.env.FINIX_APP_ID,
  FullcalendarLicenseKey: process.env.FULLCALENDAR_LICENSE_KEY || '',
  GeolocationOverrideLatLong: process.env.GEOLOCATION_OVERRIDE_LAT_LONG || '',
  IntercomApiBase: process.env.INTERCOM_API_BASE,
  IntercomAppId: process.env.INTERCOM_APP_ID,
  KingslandGWUrl: process.env.KINGSLAND_GW_URL,
  LocalNgrokUrl: process.env.LOCAL_NGROK_URL,
  PostHogHost: process.env.POSTHOG_HOST || '',
  PostHogKey: process.env.POSTHOG_KEY || '',
  StatsigApiKey: process.env.STATSIG_CLIENT_API_KEY || '',
  TurnstileSiteKey: process.env.TURNSTILE_SITE_KEY ?? '',
  VercelDeploymentSha: process.env.VERCEL_GIT_COMMIT_SHA,
}

export const isLocal = () => EnvVariables.Environment === Environments.Local
export const isProduction = () => EnvVariables.Environment === Environments.Production
export const isProductionMirror = () => EnvVariables.Environment === Environments.ProductionMirror

export const GovWellTechEmailDomain = 'govwelltech.com'
export const GovWellEmailDomain = 'govwell.com'

export const makeOrgHomePageUrl = (slug: string) => `${AppBaseUrl}/${slug}`

export const isGovWellEmail = (emailAddress?: string | null | undefined) =>
  emailAddress?.endsWith(`@${GovWellEmailDomain}`) ||
  emailAddress?.endsWith(`@${GovWellTechEmailDomain}`) ||
  false

export const EvervaultRelayUrl =
  EnvVariables.Environment === Environments.Local
    ? `https://${EnvVariables.EvervaultRelayHostname}/foreign-storage`
    : `https://${EnvVariables.EvervaultRelayHostname}/api/foreign-storage`

export const MarketingSiteUrl = 'https://govwell.com'
const appBaseUrls: Record<Environments, string> = {
  Local: 'http://localhost:8910',
  Dev: 'https://dev.govwell.com',
  Demo: 'https://demo.govwell.com',
  Prod: 'https://app.govwell.com',
  ProdCopy: 'https://mirror.govwell.io',
}
export const AppBaseUrl = appBaseUrls[EnvVariables.Environment]

export const GovWellFileUploadUrl = `${AppBaseUrl}/api/file-upload/v1`

export const WebhookBaseUrl =
  EnvVariables.Environment === Environments.Local
    ? `${EnvVariables.LocalNgrokUrl}`
    : `${AppBaseUrl}/api`

export const ElementIds = {
  SubmitComplaint: 'submit-complaint',
  ViolationEllipsis: 'violation-ellipsis-',
  ViewViolation: 'view-violation',
  DeleteViolation: 'delete-violation',
  ResolveViolation: 'resolve-violation',
  ScheduleInspection: 'schedule-inspection',
  LogInspection: 'log-inspection',
  AddComment: 'add-comment',
  EditType: 'edit-type',
  UploadFiles: 'upload-files',
  GenerateMailer: 'generate-mailer',
  NavCodeEnforcement: 'nav-code-enforcement',
  NavComplaints: 'nav-complaints',
  NavInspections: 'nav-inspections',
  ThankYouPageText: 'thank-you-page-text',
}

export const InspectionStatusToDisplayMap: Record<ViolationInspectionStatus, string> = {
  Scheduled: 'To Do',
  Cancelled: 'Cancelled',
  Completed: 'Completed',
  CompletedPassed: 'Completed - Passed',
  CompletedInViolation: 'Completed - In Violation',
}

export const ViolationStatusToDisplayMap: Record<ViolationStatus, string> = {
  Submitted: 'To Review',
  InProgress: 'Investigating',
  Closed: 'Resolved',
  Archived: 'Archived',
  InViolation: 'In Violation',
  NotInViolation: 'Not In Violation',
  Ticketed: 'Ticketed',
  // camp verde specific statuses
  CampVerdeClosed: 'Closed',
  CampVerdeAddedToCurrentCase: 'Added to Current Case',
  Abatement: 'Abatement',
  Prosecution: 'Prosecution',
  CivilHearingNotice: 'Civil Hearing Notice',
  CampVerdeAnimalCall: 'Animal Call',
  CampVerdeNotAValidAddress: 'Not a Valid Address',
}

export const ExternalLinkProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
}

export const FormFieldNamePrefix = `field-name-`

export const AcceptedAllFileTypes = '*'
export const AcceptedPdf = '.pdf'
export const AcceptedFileTypes = '.pdf, .jpeg, .jpg, .png'
export const AcceptedFileTypesWithCad =
  '.pdf, .jpeg, .jpg, .png, .dwg, .dxf, .dgn, .stl, .iges, .igs, .step, .stp'
export const AcceptedImages = '.jpeg, .jpg, .png'
export const AcceptedMp4 = '.mov, .mp4'
export const AcceptedDocx = '.docx'
export const AcceptedShp = '.shp'
export const AcceptedXlsx = '.xlsx'
export const AcceptedXls = '.xls'
export const AcceptedCsv = '.csv'

export const AcceptedFileTypesCodeEnforcementGov = `${AcceptedFileTypes}, ${AcceptedMp4}, ${AcceptedDocx}, ${AcceptedXlsx}, ${AcceptedXls}`

export const RecordTypeDisplayMap: Record<RecordTypeCategory, string> = {
  Licenses: 'Licenses',
  Permits: 'Permits',
  Violations: 'Code Enforcement',
}

export const GovWellTermsOfService = 'https://www.govwell.com/terms'

export const RecordTemplateApplicantTypeDisplayMap: Record<RecordTemplateApplicantType, string> = {
  User: 'Individuals',
  Company: 'Companies',
}

export const EmailTemplateTypeDisplayMap: Record<EmailTemplateType, string> = {
  Record: 'Link to the specific record',
  RecordSignup: 'Signup link that redirects to the specific record',
  RecordTypeApply: 'Link to submit a new record',
  NoButton: 'Do not include a button in the email',
}

export const EmailTemplateTypeButtonTextMap: Record<EmailTemplateType, string> = {
  Record: 'View in GovWell',
  RecordSignup: 'View in GovWell',
  RecordTypeApply: 'Submit a new application in GovWell',
  NoButton: '',
}

// OrgSlugs are duplicated on the BE, update there too!
export enum OrgSlugs {
  Aledo = 'aledo',
  Amherst = 'amherst',
  Athens = 'athens',
  Austin = 'austin',
  BlackMountain = 'black-mountain',
  Butner = 'butner',
  CabellCounty = 'cabell-county',
  CamdenCounty = 'camden-county',
  CampVerde = 'camp-verde',
  CapeElizabeth = 'cape-elizabeth',
  Carpinteria = 'carpinteria',
  Central = 'central',
  Centerville = 'centerville',
  Champlain = 'champlain',
  Clarksville = 'clarksville',
  Clewiston = 'clewiston',
  ClearcreekTownship = 'clearcreek-township',
  Coffeyville = 'coffeyville',
  ColdSpring = 'cold-spring',
  Collinsville = 'collinsville',
  Decatur = 'decatur',
  Duluth = 'duluth',
  Easton = 'easton',
  Eustis = 'eustis',
  Fulshear = 'fulshear',
  Fairview = 'fairview',
  GardenCity = 'garden-city',
  Gadsden = 'gadsden',
  Gonzales = 'gonzales',
  GovWell = 'govwell',
  Groves = 'groves',
  Hampton = 'hampton',
  HarrisonCounty = 'harrison-county',
  Hewitt = 'hewitt',
  Hutto = 'hutto',
  King = 'king',
  Kingsland = 'kingsland',
  LaPorte = 'laporte',
  Lewiston = 'lewiston',
  McFarland = 'mcfarland',
  Middlebury = 'middlebury',
  MtCrestedButte = 'mtcb',
  Nederland = 'nederland',
  Niles = 'niles',
  NorthRoyalton = 'north-royalton',
  OakPoint = 'oakpoint',
  Oxford = 'oxford',
  Paradise = 'paradise',
  Perinton = 'perinton',
  Piqua = 'piqua',
  Ripley = 'ripley',
  Rolesville = 'rolesville',
  Saginaw = 'saginaw',
  Shelby = 'shelby',
  StGeorge = 'st-george',
  Union = 'union',
  Vermillion = 'vermillion',
  Willis = 'willis',
  Winfield = 'winfield',
}
export const OrgHomePageHeader = 'Public Portal'

export const KB = 1024
export const MB = 1024 * KB
export const GB = 1024 * MB
export const MaxFileSize = 1 * GB

export const DatadogSettings = {
  datadogSite: 'us5.datadoghq.com',
  datadogApplicationId: 'c8cabb41-753f-478c-9357-30df956267c1',
}

export const ProcessingFeeExplanation =
  'A small processing fee is added to credit/debit card transactions to cover interchange fees charged by credit card companies like Visa.'

export const RecordTypeStatusTypeDisplayMap: Record<RecordTypeStatusType, string> = {
  ToDo: 'To Do',
  InProgress: 'In Progress',
  Complete: 'Complete',
  CompleteNegative: 'Complete - Archived/Expired',
}

export const RecordTaskStatusTypeDisplayMap: Record<RecordTaskStatus, string> = {
  ToDo: 'To Do',
  Future: 'Future',
  Done: 'Done',
}

export const PLAN_REVIEW_STAMP_LINE_HEIGHT_PX = 12
export const PLAN_REVIEW_PIXELS_PER_INCH = 72
export const PLAN_REVIEW_IMAGE_DEFAULT_WIDTH_PX = 30
// Default width of a text annotation as a fraction of the page width (0–1).
// Matches the Prisma default on `PlanReviewTextAnnotation.widthFraction`.
export const DEFAULT_TEXT_ANNOTATION_WIDTH_FRACTION = 0.3
// Cloud Callout text boxes don't have a single default width — they scale
// with the cloud's bbox via `getCloudCalloutTextWidthFraction` in
// `src/utils/cloud-callout-geometry.ts`, with `MIN_CLOUD_CALLOUT_TEXT_WIDTH_FRACTION`
// as a lower bound. See that helper for the rationale.
// Minimum width fraction enforced while resizing. 0.05 ≈ 5% of page width,
// small enough to be intentional but large enough to remain clickable/readable.
export const MIN_TEXT_ANNOTATION_WIDTH_FRACTION = 0.05
// Default fill opacity applied the first time a user enables fill on a Circle,
// Quadrilateral, or Cloud Callout. After that, the user's chosen opacity is
// remembered via `PlanSet.mostRecentlyUsedMarkupToolFillOpacity`.
export const DEFAULT_MARKUP_FILL_OPACITY = 0.5

// duplicated in file://./../../../api/src/constants/index.ts
export const GovWellTokenHeader = 'x-govwell-token'

export const RecordTaskTypeDisplayMap: Record<RecordTaskType, string> = {
  AcknowledgePlanReview: 'Acknowledge Plan Review Comments',
  AddToMeeting: 'Add to Meeting',
  ApplicationReview: 'Review Application',
  ChangeStatus: 'Change Status',
  Checklist: 'Complete Checklist Items',
  CreateChildRecord: 'Create Child Record',
  CreatePlansSetRevision: 'Create Plan Set Revision',
  CreateRequestedRecord: 'Create Requested Record',
  Custom: 'Custom',
  GenerateDocument: 'Generate Document',
  Issue: 'Issue',
  LogMeetingResult: 'Log Meeting Result',
  Migrate: 'Migrate',
  PayFees: 'Pay Fees',
  PlanReview: 'Plan Review',
  PlanReviewRouting: 'Route for Plan Review',
  Renewal: 'Renewal',
  RenewalV2: 'Renew',
  RequestFees: 'Request Fees',
  RequestRecords: 'Request Records',
  Route: 'Route for Review',
  SearchForNearbyProperties: 'Search for Nearby Properties',
  ScheduleNotifications: 'Schedule Notifications',
  ScheduleWorkflow: 'Schedule Future Workflows',
  SelectStep: 'Select Step',
  SendNotification: 'Send Email',
  SetExpiration: 'Set Expiration Date',
  UpdateApplication: 'Update Application',
  UpdatePlansSet: 'Update Plan Set',
}

export const FileOnRecordTypeDisplayMap: Record<FileOnRecordType, string> = {
  CustomTask: 'Custom Task',
  FilesNotInPlanSet: 'Additional Files',
  InspectionAttachment: 'Inspection Attachment',
  GeneratedDocument: 'Generated Document',
  InitialApplication: 'Initial Application',
  InspectionReport: 'Inspection Report',
  Invoice: 'Invoice',
  LatestPlanSet: 'Latest Plan Set',
  Receipt: 'Receipt',
}

export const AddressEntryTypeDisplayMap: Record<AddressEntryType, string> = {
  SearchAndManual: 'GIS Search and Manual Entry',
  ManualOnly: 'Manual Entry Only',
  SearchOnly: 'GIS Search Only',
}

export const AutomationTriggerTypeDisplayMap: Record<AutomationTriggerType, string> = {
  Expiration: 'Expiration',
  InspectionLogged: 'Inspection Logged',
  RecordSubmitted: 'Record Submitted',
  RecordTaskGroupComplete: 'Workflow Step Completed',
  RecordTaskGroupStart: 'Workflow Step Started',
  RecordTaskOverdue: 'Task Overdue',
  WorkflowComplete: 'Workflow Complete',
}

// The RecordFeesTabQuery may take longer than 500ms to respond locally.
// In that case, we want to increase the delay for the useRefetchExternalPayment hook
// so the query has a chance to complete.
export const RefetchExternalPaymentInterval = EnvVariables.Environment === 'Local' ? 5000 : 500

const environmentLogoTexts: Record<Environments, string | undefined> = {
  Local: 'Local',
  Dev: 'Dev',
  Demo: undefined,
  Prod: undefined,
  ProdCopy: 'Mirror',
}
export const EnvironmentLogoText = environmentLogoTexts[EnvVariables.Environment]

export const AIStaffMemberExplanation = `Members of the AI Team will see a 'Community Assistant' option in the left sidebar and receive email updates about the Community Assistant.`

export enum WindowPostMessageKey {
  AIChatClosed = 'AIChatClosed',
  AIChatOpened = 'AIChatOpened',
  AIChatReadyToReceiveSourceUrl = 'AIChatReadyToReceiveSourceUrl',
  AIChatSourceUrl = 'AIChatSourceUrl',
}
type WindowPostMessagePayload = {
  [WindowPostMessageKey.AIChatClosed]?: never
  [WindowPostMessageKey.AIChatOpened]?: never
  [WindowPostMessageKey.AIChatReadyToReceiveSourceUrl]?: never
  [WindowPostMessageKey.AIChatSourceUrl]: {
    sourceUrl: string
  }
}
export type WindowPostMessage<TKey extends WindowPostMessageKey> = {
  messageKey: TKey
} & (WindowPostMessagePayload[TKey] extends undefined
  ? { payload?: never }
  : {
      payload: WindowPostMessagePayload[TKey]
    })

export const PublicApiChatBotUrl =
  EnvVariables.Environment === Environments.Production
    ? 'https://ai-chat-bot-app.govwell.io'
    : EnvVariables.Environment === Environments.Demo
      ? 'https://ai-chat-bot-app.demo.govwell.io'
      : EnvVariables.Environment === Environments.Local
        ? 'http://localhost:8910'
        : 'https://ai-chat-bot-app.dev.govwell.io'

export const ExternalProviderDisplayMap: Record<ExternalCalendarProvider, string> = {
  Google: 'Google Calendar',
  Apple: 'Apple Calendar',
  Microsoft: 'Microsoft Outlook',
}
